import { getError } from "@remi/error";

const ABLY_ATTACH_TIMEOUT_PATTERN = "attach timed out";
const ABLY_DETACH_SUPERSEDED_PATTERN = "detach request superseded by a subsequent attach request";
const ABLY_ATTACH_SUPERSEDED_PATTERN = "attach request superseded by a subsequent detach request";
const ABLY_MAX_CHANNEL_LIMIT_PATTERN = "maximum number of channels per connection";
const ABLY_AUTH_PROVIDER_FAILURE_PATTERN = "authentication provider request failed";
const ABLY_PRESENCE_SUSPENDED_PATTERN = "presence state is out of sync due to channel being in the suspended state";
const ABLY_NETWORK_UNREACHABLE_PATTERN = "network unreachable";
// Ably wraps every string error from authCallback as ErrorInfo code 40170
const ABLY_TOKEN_CALLBACK_ERROR_CODE = 40170;
const ABLY_CLERK_SIGNED_OUT_PATTERN = "you need to supply an active session";
const ABLY_RELATED_PATTERNS = ["ably", "realtime", "ablyjs"];

const stringifyErrorLike = (errorLike: unknown) => {
  if (typeof errorLike === "string") {
    return errorLike.toLowerCase();
  }

  if (errorLike instanceof Error) {
    return `${errorLike.name} ${errorLike.message} ${errorLike.stack ?? ""}`.toLowerCase();
  }

  try {
    return JSON.stringify(errorLike).toLowerCase();
  } catch {
    return String(errorLike).toLowerCase();
  }
};

const getAblyErrorText = (errorLike: unknown) => {
  const message = getError(errorLike).message.toLowerCase();
  const stack = getAblyErrorStack(errorLike);
  const serialized = stringifyErrorLike(errorLike);
  return `${message} ${stack} ${serialized}`;
};

const getAblyErrorStack = (errorLike: unknown) => {
  if (errorLike && typeof errorLike === "object" && "stack" in errorLike && typeof errorLike.stack === "string") {
    return errorLike.stack.toLowerCase();
  }

  return "";
};

const hasAblyErrorCode = (errorLike: unknown, code: number) =>
  !!errorLike && typeof errorLike === "object" && "code" in errorLike && errorLike.code === code;

export const isAblyRelatedError = (errorLike: unknown) => {
  const text = getAblyErrorText(errorLike);
  return ABLY_RELATED_PATTERNS.some((pattern) => text.includes(pattern));
};

export const isAblyChannelLimitError = (errorLike: unknown) => {
  return getAblyErrorText(errorLike).includes(ABLY_MAX_CHANNEL_LIMIT_PATTERN);
};

export const isAblyNetworkUnreachableError = (errorLike: unknown) => {
  return getAblyErrorText(errorLike).includes(ABLY_NETWORK_UNREACHABLE_PATTERN);
};

export const isSuppressibleAblyError = (errorLike: unknown) => {
  const text = getAblyErrorText(errorLike);
  return (
    text.includes(ABLY_ATTACH_TIMEOUT_PATTERN) ||
    text.includes(ABLY_MAX_CHANNEL_LIMIT_PATTERN) ||
    text.includes(ABLY_AUTH_PROVIDER_FAILURE_PATTERN) ||
    text.includes(ABLY_DETACH_SUPERSEDED_PATTERN) ||
    text.includes(ABLY_ATTACH_SUPERSEDED_PATTERN) ||
    text.includes(ABLY_PRESENCE_SUSPENDED_PATTERN) ||
    (hasAblyErrorCode(errorLike, ABLY_TOKEN_CALLBACK_ERROR_CODE) && text.includes(ABLY_CLERK_SIGNED_OUT_PATTERN))
  );
};
